import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type CalendarRole = "tenant_admin" | "tenant_editor" | "tenant_viewer";

export type CalendarPrincipal =
  | { kind: "tenant_user"; role: CalendarRole; tenantId: string; userId: string; email?: string }
  | { kind: "superadmin"; impersonatedTenantId?: string; platformUserId: string }
  | { kind: "public_reader"; tenantId: string }
  | { kind: "embed_reader"; tenantId: string };

export interface CalendarTenantContext {
  billingClientId: string;
  dbSchema: string;
  storageRoot: string;
  tenantId: string;
  tenantSlug: string;
  umsWorkspaceId: string | null;
}

/**
 * One entry of the superadmin tenant switcher. Returned by GET /api/v1/auth/me
 * ONLY for `superadmin` principals — sourced from the calendar's own registry
 * (`public.tenants`, via CalendarService.listTenants), NOT a live CRM call.
 */
export interface TenantSummary {
  slug: string;
  name: string;
  db_schema: string;
  billing_client_id: string;
  status: string;
  enabled_tools: string[];
}

export interface SessionResponse {
  principal: CalendarPrincipal;
  tenant: CalendarTenantContext;
  email?: string;
  /** Present only for superadmins — drives the sidebar tenant switcher. */
  tenants?: TenantSummary[];
}

export function isSessionResponse(value: unknown): value is SessionResponse {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<SessionResponse>;
  if (!candidate.principal || typeof candidate.principal !== "object") return false;
  if (!("kind" in candidate.principal) || !["tenant_user", "superadmin", "public_reader", "embed_reader"].includes(String(candidate.principal.kind))) return false;
  if (!candidate.tenant || typeof candidate.tenant !== "object") return false;
  return (
    typeof candidate.tenant.tenantId === "string" &&
    typeof candidate.tenant.tenantSlug === "string" &&
    typeof candidate.tenant.dbSchema === "string"
  );
}

/**
 * The tenant a superadmin has switched into, persisted across reloads. Only
 * meaningful for `superadmin` principals; every other principal is pinned to
 * its cookie tenant server-side and this value is ignored.
 */
const ACTIVE_TENANT_KEY = "calendar.activeTenantSlug";

export function getActiveTenantSlug(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(ACTIVE_TENANT_KEY);
  } catch {
    return null;
  }
}

export function setActiveTenantSlug(slug: string | null): void {
  if (typeof window === "undefined") return;
  try {
    if (slug) window.localStorage.setItem(ACTIVE_TENANT_KEY, slug);
    else window.localStorage.removeItem(ACTIVE_TENANT_KEY);
  } catch {
    /* localStorage unavailable (private mode / SSR) — switching just won't persist. */
  }
}

interface SessionState {
  data: SessionResponse | null;
  loading: boolean;
  error?: Error;
}

const SessionContext = createContext<SessionState | null>(null);

/**
 * Detect the SPA's mount path from the live URL. Same build serves at
 * calendar.ronic.ai/ (apex, basePath="") and meetandtour.com/calendar/*
 * (prefixed, basePath="/calendar"). Vite is built with `base: './'` so
 * import.meta.env.BASE_URL is "./" — useless for routing — and we read
 * the prefix off window.location instead.
 */
export function basePath(): string {
  if (typeof window === "undefined") return "";
  const pathname = window.location.pathname;
  if (pathname === "/calendar" || pathname.startsWith("/calendar/")) {
    return "/calendar";
  }
  return "";
}

function redirectToSsoStart(): void {
  const next = window.location.pathname + window.location.search;
  window.location.href = `${basePath()}/api/v1/auth/sso/start?return=${encodeURIComponent(next)}`;
}

export function SessionProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [state, setState] = useState<SessionState>({ data: null, loading: true });

  useEffect(() => {
    let cancelled = false;
    fetch(`${basePath()}/api/v1/auth/me`, { credentials: "include" })
      .then(async (response) => {
        if (cancelled) return;
        if (response.ok) {
          const data: unknown = await response.json();
          if (!isSessionResponse(data)) {
            // Older/fail-open API builds returned a legacy tenant+user shape
            // without an authenticated principal. Never render privileged UI
            // from that response; restart the real SSO handshake instead.
            redirectToSsoStart();
            return;
          }
          setState({ data, loading: false });
          return;
        }
        if (response.status === 401) {
          redirectToSsoStart();
          return;
        }
        setState({ data: null, loading: false, error: new Error(`auth/me ${response.status}`) });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ data: null, loading: false, error: err instanceof Error ? err : new Error(String(err)) });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return <SessionContext.Provider value={state}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used within <SessionProvider>");
  return ctx;
}

export function calendarCsrfToken():string {
  if(typeof document==='undefined')return '';
  return document.cookie.split(';').map(part=>part.trim()).find(part=>part.startsWith('__Host-ronic-calendar-csrf='))?.slice('__Host-ronic-calendar-csrf='.length)||'';
}

export async function signOut():Promise<void> {
  const response=await fetch(`${basePath()}/api/v1/auth/logout`,{method:'POST',credentials:'include',headers:{'x-csrf-token':calendarCsrfToken()}});
  if(!response.ok)throw new Error('Could not sign out. Please try again.');
  setActiveTenantSlug(null);
  window.location.href=`${basePath()}/api/v1/auth/signed-out`;
}
